import React, { Fragment, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { Select } from 'components/forms'
import { branch } from 'recompose'
import query from 'gql/query/cashRegistersTree'
import { useQuery } from 'components/graphql'
import { withFormState } from 'informed'
import get from 'lodash/get'

const ClientSelector = ({ formState, lastNode, ...props }) => {
	const { data: cashRegistersTree = {} } = useQuery(query, { fetchPolicy: 'cache-first' })
	const { field, noForm, initialValue } = props
	const [t] = useTranslation()

	const initialNodeId = noForm ? initialValue : formState.values[field]
	const error = noForm ? props.error : get(formState.errors, field)

	let depth, typeName
	switch(lastNode) {
		case 'DEPARTMENT':
			depth = 1
			typeName = 'Department'
			break
		case 'REGION':
			depth = 2
			typeName = 'Region'
			break
		case 'SHOP':
			depth = 3
			typeName = 'Shop'
			break
		case 'CASH_REGISTER':
			depth = 4
			typeName = 'CashRegister'
			break
		default:
			depth = 4
			typeName = 'CashRegister'
			break
	}

	const findInitialNode = (addresArr = [], { children, ...rest }) => {
		if(!children) {
			return false
		}
		const res = children.find(node => {
			if(node.id === initialNodeId && node.__typename === typeName) {
				addresArr.unshift(node.id)
				return true
			}
			const isAddress = findInitialNode(addresArr, node)
			if(isAddress) {
				addresArr.unshift(node.id)
			}
			return isAddress
		})
		return !!res
	}

	let nodeAddress = []
	findInitialNode(nodeAddress, cashRegistersTree)

	return(
		<ClientSelectorNode
			data={cashRegistersTree.children}
			labels={[
				t('options_section.strings.department'),
				t('options_section.strings.region'),
				t('options_section.strings.branch'),
				t('options_section.strings.cash_register')
			]}
			address={nodeAddress}
			depth={depth}
			error={error}
			{...props}
		/>
	)
}

ClientSelector.propTypes = {
	lastNode: PropTypes.oneOf(['DEPARTMENT', 'REGION', 'SHOP', 'CASH_REGISTER']).isRequired,
	onChange: PropTypes.func
}

const ClientSelectorNode = ({ data = [], labels, address, depth, ...props }) => {
	const [curAddress = '', ...nextAddress] = address
	const [nodeId, setNode] = useState(curAddress)
	useEffect(() => {
		setNode(curAddress)
	}, [curAddress])
	let nextData
	if(!nodeId) {
		nextData = []
	} else {
		const nextNode = data.find(({ id }) => id === nodeId) || {}
		nextData = nextNode.children || []
	}
	const [label, ...nextLabels] = labels
	const newDepth = depth - 1
	const isLastNode = newDepth === 0
	const onChange = newNode => {
		setNode(newNode.id)
		if(newDepth === 0 && props.onChange) {
			props.onChange(newNode.id)
		}
	}

	const sortedOptions = [...data].sort((a, b) => {
		if (a.name < b.name) {
		  return -1
		}
		if (a.name > b.name) {
		  return 1
		}
		return 0
	})

	const selectProps = {
		noForm: !isLastNode,
		label,
		options: sortedOptions,
		valueField: 'id',
		labelField: 'name',
		value: nodeId,
		onChange: onChange,
		error: !nextData.length && props.error,
		...isLastNode && props
	}
	return (
		<Fragment>
			<Select style={data.length ? selectProps.style : { display: 'none', ...selectProps.style }} {...selectProps}/>
			{!isLastNode &&
				<ClientSelectorNode data={nextData} labels={nextLabels} depth={newDepth} address={nextAddress} {...props}/>
			}
		</Fragment>
	)
}

export default branch(
	({ noForm }) => !noForm,
	withFormState
)(ClientSelector)
